import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DollarSign, CheckCircle, ArrowLeft, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import Header from "@/components/Header";

const TIP_PACKAGES = [
  { id: "small", amount: 2, label: "$2", emoji: "A little love" },
  { id: "medium", amount: 5, label: "$5", emoji: "Solid support" },
  { id: "large", amount: 10, label: "$10", emoji: "Big fan energy" },
  { id: "mega", amount: 25, label: "$25", emoji: "Super supporter" },
  { id: "super", amount: 50, label: "$50", emoji: "Legend status" },
];

export default function TipArtist() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const aiCharacterName = searchParams.get("ai");
  const aiCharacterId = searchParams.get("ai_id");
  const { user } = useAuth();
  const [artist, setArtist] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState("small");
  const [loading, setLoading] = useState(false);

  const fetchArtist = useCallback(async () => {
    const res = await fetch(`${API}/profile/${userId}`, { credentials: "include" });
    if (res.ok) setArtist(await res.json());
  }, [userId]);

  useEffect(() => {
    if (userId) fetchArtist();
  }, [userId, fetchArtist]);

  const handleTip = async () => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const res = await fetch(`${API}/tips/create-session`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          artist_user_id: userId,
          package_id: selectedPackage,
          origin_url: originUrl,
          ai_character_id: aiCharacterId || undefined,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      const data = await res.json();
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.message);
      setLoading(false);
    }
  };

  if (!artist) return (
    <div className="min-h-screen flex flex-col" style={{ background: "linear-gradient(180deg, #000022 0%, #000044 100%)" }}>
      <Header />
      <div className="flex-1 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FF00FF] border-t-transparent rounded-full animate-spin" />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "linear-gradient(180deg, #000022 0%, #000044 100%)" }}>
      <Header />
      <main className="flex-1 container mx-auto px-4 py-6 max-w-lg">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#FF66FF] hover:text-[#00FFFF] mb-6" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" /> Back
        </button>

        <div className="myspace-card">
          <div className="text-center mb-6">
            <DollarSign className="w-10 h-10 mx-auto text-[#00FF00] mb-2" />
            <h1 className="text-xl font-bold text-[#00FFFF]" style={{ textShadow: "0 0 10px #00FFFF" }} data-testid="tip-page-title">
              Tip {artist.name}
            </h1>
            {artist.artist_verified && (
              <span className="inline-flex items-center gap-1 text-xs text-[#FFD700] mt-1">
                <CheckCircle className="w-3 h-3" /> Verified Artist
              </span>
            )}
            {aiCharacterName && (
              <div
                className="mt-3 px-2 py-1 border border-[#FF00FF] bg-[rgba(255,0,255,0.1)] text-xs text-[#FF66FF] inline-flex items-center gap-1"
                data-testid="tip-ai-context"
              >
                <Sparkles className="w-3 h-3" />
                For their AI date <span className="text-[#00FFFF] font-bold">{aiCharacterName}</span>
              </div>
            )}
          </div>

          <div className="space-y-3 mb-6">
            {TIP_PACKAGES.map(pkg => (
              <button key={pkg.id} onClick={() => setSelectedPackage(pkg.id)}
                className={`w-full p-3 border-2 text-left transition-all flex items-center justify-between ${
                  selectedPackage === pkg.id
                    ? "border-[#00FF00] bg-[rgba(0,255,0,0.1)]"
                    : "border-[#6600CC] hover:border-[#FF00FF]"
                }`}
                data-testid={`tip-package-${pkg.id}`}
              >
                <div>
                  <span className="text-lg font-bold text-[#00FFFF]">{pkg.label}</span>
                  <span className="text-sm text-[#99CCFF] ml-2">{pkg.emoji}</span>
                </div>
                {selectedPackage === pkg.id && <CheckCircle className="w-5 h-5 text-[#00FF00]" />}
              </button>
            ))}
          </div>

          <button onClick={handleTip} disabled={loading} className="myspace-btn-primary w-full text-lg py-3 disabled:opacity-50" data-testid="send-tip-btn">
            {loading ? "Processing..." : `Send ${TIP_PACKAGES.find(p => p.id === selectedPackage)?.label} Tip`}
          </button>
        </div>
      </main>
    </div>
  );
}

export function TipSuccess() {
  const [searchParams] = useState(new URLSearchParams(window.location.search));
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    if (sessionId) pollStatus(sessionId, 0);
  }, [sessionId]);

  const pollStatus = async (sid, attempt) => {
    if (attempt >= 5) { setStatus("timeout"); return; }
    try {
      const res = await fetch(`${API}/tips/status/${sid}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
      }
    } catch (e) { /* Payment status polling retry */ }
    setTimeout(() => pollStatus(sid, attempt + 1), 2000);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="myspace-card text-center max-w-md w-full">
        {status === "checking" && (
          <>
            <div className="w-12 h-12 border-4 border-[#00FFFF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-[#00FFFF]">Checking payment...</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle className="w-16 h-16 mx-auto text-[#00FF00] mb-4" />
            <h2 className="text-2xl font-bold text-[#00FF00] mb-2" style={{ textShadow: "0 0 10px #00FF00" }}>Tip Sent!</h2>
            <p className="text-[#99CCFF] mb-6">Thank you for supporting the artist!</p>
            <a href="/music" className="myspace-btn-primary inline-block" data-testid="back-to-music-btn">Back to Music Hub</a>
          </>
        )}
        {(status === "timeout" || status === "expired") && (
          <>
            <p className="text-[#FF6600] text-lg font-bold mb-4">Payment could not be verified</p>
            <a href="/music" className="myspace-btn inline-block">Back to Music Hub</a>
          </>
        )}
      </div>
    </div>
  );
}

export function TipCancel() {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="myspace-card text-center max-w-md w-full">
        <p className="text-[#FF6600] text-lg font-bold mb-4">Tip Cancelled</p>
        <p className="text-[#99CCFF] mb-6">No worries - you can tip anytime!</p>
        <a href="/music" className="myspace-btn-primary inline-block" data-testid="back-to-music-btn">Back to Music Hub</a>
      </div>
    </div>
  );
}
